/**
 * #1134 — display identity for THIS account's own Kalshi tickers, kept after
 * the live market row is gone.
 *
 * A CFB total (`KXNCAAFTOTAL-26OCT10BSUFRES-49`) packs both college codes into
 * one token with no boundary, so `describeMarket` can only name the matchup
 * from the producer's canonical away/home on the live frame. Two things take
 * that frame away while the fill or position is still on screen:
 *
 *   • retirement — Hydra withdraws a finished market (`applyMarketRemoved`),
 *     and `pruneStaleMarkets` drops any terminal row quiet for 30 minutes;
 *   • reload — a new tab or popup starts with an empty store, and the venue
 *     no longer lists a settled market.
 *
 * So the producer's identity for an account ticker is copied here, keyed by
 * the exact ticker, and `describeMarket` reads it only when the store has NO
 * live row for that ticker. It is never a second authority while a row is
 * live: the live row's answer wins, including a row WITHOUT a pair (Hydra
 * leaves a CFB line unstamped when the event pair is missing or contradicts
 * the ticker). A newly stamped, different pair replaces what is kept; an
 * unstamped frame leaves it alone, so a producer gap cannot erase the last
 * pair Hydra stamped for this exact ticker, and two feeds that disagree on
 * stamping cannot make it flap.
 *
 * Kept, per ticker: the canonical away/home codes, nothing else. The line
 * needs no copy: the venue's floor strike equals the ticker's own dated rule
 * (`kalshiTotalLineFromStrike`, and N − 0.5 for spreads — checked against the
 * BSU/FRES totals and spreads on 2026-10-12), which describeMarket already
 * falls back to. NOT kept: prices, books, volume, status — nothing tradable.
 *
 * Bounds (the ticket's "no unbounded global retention"):
 *   • only tickers the wiring names (`accountIdentityRetention.ts`): the
 *     account's own fills and positions whose label can take a producer pair;
 *   • one list per user + organization, so a switched-in account never reads
 *     the previous trader's bets;
 *   • at most RETAINED_IDENTITY_CAP entries per list, oldest-seen first, plus
 *     any ticker the account still names (never evicted, so a large book
 *     cannot churn; bounded by the account's own fills and open positions —
 *     recent fills are capped by the store, positions by what the trader
 *     holds). A list is not capped on load, before the account has named
 *     anything, so a held ticker cannot be cut from an over-full list;
 *   • an entry expires about RETAINED_IDENTITY_TTL_MS after the account last
 *     named its ticker (`seenMs` is refreshed at most daily); expired entries
 *     and wholly expired lists are dropped on load.
 *
 * Cost: a label read is one Map lookup, made only when the store has no live
 * row. Every admitted Kalshi or Polymarket frame costs one Set lookup at
 * ingest; a frame for a named ticker also compares two short strings. Nothing here polls,
 * fetches or adds feed demand. Storage is localStorage, written at most once
 * per PERSIST_DELAY_MS and when the feed tears down (`pagehide` on a real
 * unload; a page frozen in the back/forward cache runs no timers, so up to one
 * second of captures can be lost if the browser later evicts it), and every
 * access is guarded: without storage the in-memory copy still covers
 * retirement. When two tabs disagree on a pair, the merge keeps the entry
 * with the later `seenMs` (set when its pair changes, refreshed at most daily
 * while named); a tie goes to this tab.
 *
 * Deliberately store-free (no marketStore/accountStore import): marketStore
 * calls into this file, so the wiring that reads both stores lives in
 * `accountIdentityRetention.ts`.
 */
import { create } from 'zustand';

export interface RetainedIdentity {
  awayCode: string;
  homeCode: string;
  /** Last time the account named this ticker (epoch ms). Drives expiry. */
  seenMs: number;
}

/** The identity fields a live frame can offer. */
export interface LiveIdentity {
  canonicalAwayCode?: string | null;
  canonicalHomeCode?: string | null;
  floorStrike?: number | null;
}

export const RETAINED_IDENTITY_CAP = 300;
export const RETAINED_IDENTITY_TTL_MS = 30 * 24 * 60 * 60_000;
const PERSIST_DELAY_MS = 1_000;
/** Re-stamp `seenMs` at most this often, so a 10 s fills poll is not a write. */
const SEEN_REFRESH_MS = 24 * 60 * 60_000;
const STORAGE_PREFIX = 'g1_account_market_identity_v1:';

let _scope: string | null = null;
const _byTicker = new Map<string, RetainedIdentity>();
/** Relay-saved display evidence. Session-only; never a market/book or URL. */
export interface SavedHistoricalIdentity {
  description: string;
  proposition: string | null;
  awayCode: string | null;
  homeCode: string | null;
  pairConflicted: boolean;
}
const _historical = new Map<string, SavedHistoricalIdentity>();
export type HistoricalReadKind = 'fills' | 'positions' | 'realized';
/** Three existing financial readers; each bounded to 5,000 exact tickers.
 * Replacing one snapshot cannot erase the other reader's historical labels. */
const _historicalReads = new Map<HistoricalReadKind, Map<string, SavedHistoricalIdentity>>();
function sameHistoricalIdentity(a: SavedHistoricalIdentity | undefined, b: SavedHistoricalIdentity): boolean {
  return !!a && a.description === b.description && a.proposition === b.proposition
    && a.awayCode === b.awayCode && a.homeCode === b.homeCode && a.pairConflicted === b.pairConflicted;
}
let _historicalEpoch = 0;
export const historicalIdentityEpoch = (): number => _historicalEpoch;
export function invalidateHistoricalIdentitySession(): void {
  _historicalEpoch++;
  clearHistoricalIdentities();
}
let _relevant: ReadonlySet<string> = new Set();
let _namedIdentityCount = 0;
// Only current account tickers. Presence is separate from the saved pair:
// an unstamped live row must replace the saved label until that row retires.
const _liveIdentity = new Map<string, LiveIdentity>();
let _persistTimer: ReturnType<typeof setTimeout> | null = null;

/** Saved identity and relevant live presence/pair/line transitions only.
 *  ActivityTicker must follow withdrawal as well as late identity, without
 *  subscribing to repeat price frames. */
export const useAccountIdentityVersion = create<{ version: number }>(() => ({ version: 0 }));

function bump(): void {
  useAccountIdentityVersion.setState((s) => ({ version: s.version + 1 }));
}

/** The kept identity for this exact ticker, or null. O(1). */
export function retainedIdentity(ticker: string): RetainedIdentity | null {
  if (_historical.get(ticker)?.pairConflicted) return null;
  return _byTicker.get(ticker) ?? null;
}

export function savedHistoricalIdentity(ticker: string): SavedHistoricalIdentity | null {
  return _historical.get(ticker) ?? null;
}

export function clearHistoricalIdentities(): void {
  _historicalReads.clear();
  if (!_historical.size) return;
  _historical.clear();
  bump();
}

/** Exact current-account REST sibling, bounded by its validated financial set.
 * Bad optional metadata cannot invalidate fills or introduce another account.
 * A valid empty sibling withdraws saved evidence; missing/malformed retains it.
 * Pair evidence is display-only and deliberately never enters _byTicker. */
export function hydrateHistoricalIdentities(
  viewer: { id: string; orgId: string | null },
  envelope: unknown,
  financialTickers: readonly string[],
  kind: HistoricalReadKind = 'fills',
): boolean {
  if (!['fills', 'positions', 'realized'].includes(kind)) return false;
  if (_scope !== `${viewer.id}|${viewer.orgId ?? ''}` || financialTickers.length > 5000
      || !envelope || typeof envelope !== 'object' || Array.isArray(envelope)) return false;
  const e = envelope as Record<string, unknown>;
  if (e.userId !== viewer.id || e.orgId !== viewer.orgId || !Array.isArray(e.identities)
      || e.identities.length > 5000) return false;
  const allowed = new Set(financialTickers);
  const next = new Map<string, SavedHistoricalIdentity>();
  const text = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= 360
    && v.trim() === v && !/[\u0000-\u001f\u007f]/.test(v);
  const code = (v: unknown): v is string => typeof v === 'string' && /^[A-Z0-9._-]{1,80}$/.test(v);
  for (const row of e.identities) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
    const r = row as Record<string, unknown>;
    if (typeof r.pairConflicted !== 'boolean' || !r.identity || typeof r.identity !== 'object'
        || Array.isArray(r.identity)) return false;
    const i = r.identity as Record<string, unknown>;
    if (typeof i.ticker !== 'string' || !/^KX[A-Z0-9._-]{1,253}$/.test(i.ticker)
        || !allowed.has(i.ticker) || next.has(i.ticker) || !text(i.description)
        || (i.proposition !== null && !text(i.proposition))
        || (i.source !== 'hydracore' && i.source !== 'kalshi-event')
        || !Number.isSafeInteger(i.receivedMs) || Number(i.receivedMs) < 0
        || ((i.awayCode === null) !== (i.homeCode === null))
        || (i.awayCode !== null && (!code(i.awayCode) || !code(i.homeCode) || i.awayCode === i.homeCode))
        || (i.source === 'kalshi-event' && i.awayCode !== null)) return false;
    const kept = _byTicker.get(i.ticker);
    const conflict = r.pairConflicted || !!(i.awayCode && kept
      && (kept.awayCode !== i.awayCode || kept.homeCode !== i.homeCode));
    next.set(i.ticker, {
      description: i.description, proposition: i.proposition as string | null,
      awayCode: conflict ? null : i.awayCode as string | null,
      homeCode: conflict ? null : i.homeCode as string | null, pairConflicted: conflict,
    });
  }
  const previousRead = _historicalReads.get(kind);
  if (previousRead?.size === next.size && [...next].every(([ticker, row]) => sameHistoricalIdentity(previousRead.get(ticker), row))) return true;
  _historicalReads.set(kind, next);
  const combined = new Map<string, SavedHistoricalIdentity>();
  for (const read of _historicalReads.values()) for (const [ticker, row] of read) {
    const previous = combined.get(ticker);
    if (!previous) { combined.set(ticker, row); continue; }
    const conflict = previous.pairConflicted || row.pairConflicted || !!(previous.awayCode && row.awayCode
      && (previous.awayCode !== row.awayCode || previous.homeCode !== row.homeCode));
    const preferred = previous.awayCode && !row.awayCode ? previous : row;
    combined.set(ticker, conflict ? { ...preferred, awayCode: null, homeCode: null, pairConflicted: true } : preferred);
  }
  const changed = combined.size !== _historical.size || [...combined].some(([ticker, row]) =>
    !sameHistoricalIdentity(_historical.get(ticker), row));
  if (changed) {
    _historical.clear();
    for (const [ticker, row] of combined) _historical.set(ticker, row);
    bump();
  }
  return true;
}

/** Called from marketStore ingest for every admitted market frame. */
export function noteMarketIdentity(market: { ticker: string } & LiveIdentity): void {
  if (!_relevant.has(market.ticker)) return;
  const displayChanged = observeLive(market.ticker, market);
  if (applyLive(market.ticker, market, Date.now()) || displayChanged) bump();
}

/** Called only after the authoritative store actually removes a live row. */
export function noteMarketIdentityRemoved(ticker: string): void {
  if (_liveIdentity.delete(ticker) && _relevant.has(ticker)) bump();
}

function observeLive(ticker: string, live: LiveIdentity): boolean {
  const prev = _liveIdentity.get(ticker);
  if (prev && prev.canonicalAwayCode === live.canonicalAwayCode &&
      prev.canonicalHomeCode === live.canonicalHomeCode && prev.floorStrike === live.floorStrike) return false;
  _liveIdentity.set(ticker, {
    canonicalAwayCode: live.canonicalAwayCode,
    canonicalHomeCode: live.canonicalHomeCode,
    floorStrike: live.floorStrike,
  });
  return true;
}

/**
 * The account's current tickers. Newly named tickers are read from the live
 * store through `readLive` (the frame may have landed before the fill).
 */
export function noteRelevantTickers(
  tickers: Iterable<string>,
  readLive: (ticker: string) => LiveIdentity | null | undefined,
): void {
  const next = new Set(tickers);
  const prev = _relevant;
  _relevant = next;
  // Membership changes at account publication cadence, not market cadence.
  // Count once so capturing a large held book cannot rescan it per frame.
  _namedIdentityCount = 0;
  for (const ticker of _byTicker.keys()) if (next.has(ticker)) _namedIdentityCount++;
  for (const ticker of _liveIdentity.keys()) if (!next.has(ticker)) _liveIdentity.delete(ticker);
  if (_scope == null) return;
  const now = Date.now();
  let changed = false;
  let touched = false;
  for (const ticker of next) {
    if (!prev.has(ticker)) {
      const live = readLive(ticker);
      if (live) {
        const displayChanged = observeLive(ticker, live);
        if (applyLive(ticker, live, now) || displayChanged) {
          changed = true;
          continue;
        }
      }
    }
    const kept = _byTicker.get(ticker);
    if (kept && now - kept.seenMs >= SEEN_REFRESH_MS) {
      kept.seenMs = now;
      touched = true;
    }
  }
  if (touched && !changed) schedulePersist();
  if (changed) bump();
}

/**
 * Switch to another user/organization's list (null = signed out). The
 * previous list is written out and dropped from memory; the new one loads.
 * Returns whether the scope changed.
 */
export function setIdentityScope(scope: string | null): boolean {
  if (scope === _scope) return false;
  _historicalEpoch++;
  flushPersist();
  const hadEntries = _byTicker.size > 0 || _historical.size > 0;
  _byTicker.clear();
  _historical.clear();
  _historicalReads.clear();
  _relevant = new Set();
  _namedIdentityCount = 0;
  _liveIdentity.clear();
  _scope = scope;
  if (scope != null) {
    const now = Date.now();
    sweepExpiredLists(now);
    // Uncapped: nothing is named yet, so a cap here could cut a held ticker.
    for (const [ticker, entry] of readStored(scope, now, false)) _byTicker.set(ticker, entry);
  }
  if (hadEntries || _byTicker.size > 0) bump();
  return true;
}

/** Keep a live row's stamped pair. An unstamped row changes nothing here (it
 *  still wins at read time while live). Returns whether the kept pair changed. */
function applyLive(ticker: string, live: LiveIdentity, now: number): boolean {
  if (_scope == null) return false;
  const away = live.canonicalAwayCode?.trim().toUpperCase();
  const home = live.canonicalHomeCode?.trim().toUpperCase();
  if (!away || !home) return false;
  const kept = _byTicker.get(ticker);
  if (kept && kept.awayCode === away && kept.homeCode === home) return false;
  if (!kept) _namedIdentityCount++;
  _byTicker.set(ticker, { awayCode: away, homeCode: home, seenMs: now });
  enforceCap(_byTicker, _namedIdentityCount);
  schedulePersist();
  return true;
}

/** Oldest-seen first among tickers the account no longer names; a named one
 *  is never evicted. */
function enforceCap(map: Map<string, RetainedIdentity>, namedCount?: number): void {
  if (map.size <= RETAINED_IDENTITY_CAP) return;
  if (namedCount === map.size) return; // Every entry is protected; no eviction work.
  const evictable = [...map.entries()]
    .filter(([t]) => !_relevant.has(t))
    .sort((a, b) => a[1].seenMs - b[1].seenMs);
  for (const [ticker] of evictable) {
    if (map.size <= RETAINED_IDENTITY_CAP) break;
    map.delete(ticker);
  }
}

function schedulePersist(): void {
  if (_persistTimer != null) return;
  _persistTimer = setTimeout(flushPersist, PERSIST_DELAY_MS);
}

/** Write the current list now (merged with what another tab may have written). */
export function flushPersist(): void {
  if (_persistTimer != null) {
    clearTimeout(_persistTimer);
    _persistTimer = null;
  }
  if (_scope == null) return;
  const now = Date.now();
  const merged = readStored(_scope, now);
  for (const [ticker, entry] of _byTicker) {
    const other = merged.get(ticker);
    if (!other || other.seenMs <= entry.seenMs) merged.set(ticker, entry);
  }
  enforceCap(merged);
  const rows = [...merged].map(([t, e]) => [t, e.awayCode, e.homeCode, e.seenMs]);
  try {
    localStorage.setItem(STORAGE_PREFIX + _scope, JSON.stringify({ v: 1, rows }));
  } catch {
    // Storage full, blocked or absent: the in-memory copy still covers this tab.
  }
}

function readStored(scope: string, now: number, cap = true): Map<string, RetainedIdentity> {
  const out = new Map<string, RetainedIdentity>();
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_PREFIX + scope);
  } catch {
    return out;
  }
  if (!raw) return out;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return out;
  }
  const rows = (parsed as { rows?: unknown })?.rows;
  if (!Array.isArray(rows)) return out;
  for (const row of rows) {
    if (!Array.isArray(row) || row.length !== 4) continue;
    const [ticker, away, home, seenMs] = row as unknown[];
    if (typeof ticker !== 'string' || !ticker) continue;
    if (typeof away !== 'string' || !away || typeof home !== 'string' || !home) continue;
    if (typeof seenMs !== 'number' || !Number.isFinite(seenMs)) continue;
    if (now - seenMs > RETAINED_IDENTITY_TTL_MS) continue;
    out.set(ticker, { awayCode: away, homeCode: home, seenMs });
  }
  if (cap) enforceCap(out);
  return out;
}

/** Remove other accounts' lists once every entry in them has expired, so a
 *  shared browser does not keep a departed trader's tickers forever. */
function sweepExpiredLists(now: number): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    for (const key of keys) {
      if (readStored(key.slice(STORAGE_PREFIX.length), now).size === 0) localStorage.removeItem(key);
    }
  } catch {
    // Unreadable storage: nothing to sweep.
  }
}

/** Test seam: drop all in-memory state, as a page reload would. Storage is kept. */
export function _resetAccountIdentityForTests(): void {
  if (_persistTimer != null) clearTimeout(_persistTimer);
  _persistTimer = null;
  _scope = null;
  _historicalEpoch = 0;
  _byTicker.clear();
  _historical.clear();
  _historicalReads.clear();
  _relevant = new Set();
  _namedIdentityCount = 0;
  _liveIdentity.clear();
  useAccountIdentityVersion.setState({ version: 0 });
}
